import api from "./api";
import type {
  CreateOrderPayload,
  UpdateOrderPayload,
  MarkOrderPaymentPayload,
  Order,
  OrderDetail,
  OrderListResponse,
} from "../types/models";

export interface OrderListParams {
  status?: string[] | null;
  customer_id?: string | null;
  source?: string | null; // 'portal' for portal orders only (Story 7-7)
  from_date?: string | null;
  to_date?: string | null;
  date_field?: "created_at" | "updated_at" | null;
  search?: string | null;
  payment_status?: string | null; // 'paid' | 'unpaid' | 'overdue' (Story 11.6-1)
  page?: number;
  limit?: number;
}

/**
 * Create a new order (customer + line items). Story 4.3
 */
export async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const response = await api.post<Order>("/orders", payload);
  return response.data;
}

/**
 * Get order by ID (full detail: customer, items, status history). Story 4.5
 */
export async function getOrderById(id: string): Promise<OrderDetail> {
  const response = await api.get<OrderDetail>(`/orders/${id}`);
  return response.data;
}

/**
 * List orders with filters and pagination. Story 4.4
 */
export async function getOrders(params: OrderListParams = {}): Promise<OrderListResponse> {
  const { status, customer_id, source, from_date, to_date, date_field, search, payment_status, page = 1, limit = 20 } = params;
  const queryParams: Record<string, string | number> = { page, limit };
  if (status?.length) queryParams.status = status.join(",");
  if (customer_id) queryParams.customer_id = customer_id;
  if (source === "portal") queryParams.source = "portal";
  if (from_date) queryParams.from_date = from_date;
  if (to_date) queryParams.to_date = to_date;
  if (date_field) queryParams.date_field = date_field;
  if (search?.trim()) queryParams.search = search.trim();
  if (payment_status) queryParams.payment_status = payment_status;
  const response = await api.get<OrderListResponse>("/orders", {
    params: queryParams,
  });
  return response.data;
}

/**
 * Update an existing order (Story 4.6).
 * Order can only be edited when status is "created" or "to_validate".
 */
export async function updateOrder(id: string, payload: UpdateOrderPayload): Promise<Order> {
  const response = await api.put<Order>(`/orders/${id}`, payload);
  return response.data;
}

export interface ChangeOrderStatusPayload {
  status: string;
  notes?: string;
}

/**
 * Change order status (Story 4.7). PATCH /api/orders/{id}/status.
 * Returns full order detail with updated status_history.
 */
export async function changeOrderStatus(
  id: string,
  payload: ChangeOrderStatusPayload
): Promise<OrderDetail> {
  const response = await api.patch<OrderDetail>(`/orders/${id}/status`, payload);
  return response.data;
}

/**
 * Mark an order paid/unpaid (Story 11.6-1). PATCH /api/orders/{id}/payment.
 * Returns full order detail.
 */
export async function markOrderPayment(
  id: string,
  payload: MarkOrderPaymentPayload
): Promise<OrderDetail> {
  const response = await api.patch<OrderDetail>(`/orders/${id}/payment`, payload);
  return response.data;
}

export interface AssignDriverPayload {
  assigned_driver_id: string | null;
}

/**
 * Assign or unassign driver for order (Story 8-2). PATCH /api/orders/{id}/driver.
 * Admin/manager only.
 */
export async function assignOrderDriver(
  id: string,
  payload: AssignDriverPayload
): Promise<OrderDetail> {
  const response = await api.patch<OrderDetail>(`/orders/${id}/driver`, payload);
  return response.data;
}
